<template>
  <div class="min-h-screen flex items-center justify-center bg-white dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
    <div class="w-full max-w-md">
      <div id="reset-password-card" class="w-full">
          <!-- Logo -->
          <div class="flex justify-center mb-6">
            <div class="h-16 w-16 bg-primary-600 dark:bg-primary-500 rounded-xl flex items-center justify-center shadow-sm">
              <svg class="h-9 w-9 text-white" fill="currentColor" viewBox="0 0 24 24">
                <path d="M9 4C9 3.44772 9.44772 3 10 3H14C14.5523 3 15 3.44772 15 4V6C15 6.55228 14.5523 7 14 7H10C9.44772 7 9 6.55228 9 6V4Z"/>
                <rect x="10" y="7" width="4" height="8" rx="0.5" fill="currentColor"/>
                <circle cx="12" cy="17" r="1" fill="currentColor"/>
                <path d="M11 10L11.5 10.5L13 9" stroke="currentColor" stroke-width="0.8" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </div>
          </div>

          <!-- Title Section -->
          <div class="text-center mb-8">
            <h1 class="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              {{ t('auth.resetPassword.title') }}
            </h1>
            <p class="text-sm text-gray-600 dark:text-gray-400">
              {{ t('auth.resetPassword.subtitle') }}
            </p>
          </div>
          <!-- Reset Password Form -->
          <HLForm v-if="!resetComplete" :model="form" @submit.prevent="handleResetPassword">
            <HLSpace vertical :size="20">
              <!-- Token Error Alert -->
              <HLAlert 
                v-if="tokenError" 
                type="error" 
                :title="tokenError"
                show-icon
                closable
                @close="tokenError = ''"
              />

              <!-- New Password -->
              <HLFormItem 
                :label="t('auth.resetPassword.newPasswordLabel')" 
                path="newPassword" 
                :validation-status="errors.newPassword ? 'error' : undefined" 
                required
              >
                <HLInput
                  id="newPassword"
                  v-model:modelValue="form.newPassword"
                  type="password"
                  :placeholder="t('auth.resetPassword.newPasswordPlaceholder')"
                  size="lg"
                  autocomplete="new-password"
                  :disabled="loading || !hasValidToken"
                />
                <template v-if="errors.newPassword" #feedback>{{ errors.newPassword }}</template>
                <template v-else #extra>
                  <span class="text-xs text-gray-500">{{ t('auth.resetPassword.passwordHint') }}</span>
                </template>
              </HLFormItem>

              <!-- Confirm Password -->
              <HLFormItem 
                :label="t('auth.resetPassword.confirmPasswordLabel')" 
                path="confirmPassword" 
                :validation-status="errors.confirmPassword ? 'error' : undefined" 
                required
              >
                <HLInput
                  id="confirmPassword"
                  v-model:modelValue="form.confirmPassword"
                  type="password"
                  :placeholder="t('auth.resetPassword.confirmPasswordPlaceholder')"
                  size="lg"
                  autocomplete="new-password"
                  :disabled="loading || !hasValidToken"
                />
                <template v-if="errors.confirmPassword" #feedback>{{ errors.confirmPassword }}</template>
              </HLFormItem>

              <HLSpace vertical :size="16">
                <HLButton
                  id="reset-password-submit"
                  color="blue"
                  variant="primary"
                  size="lg"
                  :disabled="loading || !hasValidToken"
                  :loading="loading"
                  block
                  @click="handleResetPassword"
                >
                  {{ loading ? t('auth.resetPassword.resetting') : t('auth.resetPassword.resetPassword') }}
                </HLButton>

                <div class="text-center">
                  <HLButton 
                    id="back-to-login-link" 
                    href="/login" 
                    variant="text" 
                    color="blue" 
                    size="sm"
                  >
                    {{ t('auth.resetPassword.backToLogin') }}
                  </HLButton>
                </div>
              </HLSpace>
            </HLSpace>
          </HLForm>

          <!-- Success State -->
          <HLSpace v-else vertical :size="20">
            <!-- Success Alert -->
            <HLAlert
              type="success"
              :title="t('auth.resetPassword.successTitle')"
              :description="t('auth.resetPassword.successMessage')"
              show-icon
            />

            <HLButton
              id="go-to-login-btn"
              href="/login"
              color="blue"
              variant="primary"
              size="lg"
              block
            >
              {{ t('auth.resetPassword.goToLogin') }}
            </HLButton>
          </HLSpace>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { http } from '@/utils/httpClient'
import { useToast } from 'vue-toastification'
import { 
  HLButton, 
  HLInput, 
  HLForm, 
  HLFormItem, 
  HLSpace, 
  HLAlert
} from '@platform-ui/highrise'
import { t } from '@/i18n/en-us'

const route = useRoute()
const router = useRouter()
const toast = useToast()

// Reactive data
const loading = ref(false)
const resetComplete = ref(false)
const tokenError = ref('')
const form = reactive({
  newPassword: '',
  confirmPassword: ''
})

const errors = reactive({
  newPassword: '',
  confirmPassword: ''
})

// Computed
const hasValidToken = computed(() => {
  return !!route.query.token && !tokenError.value
})

// Methods
const validateForm = () => {
  errors.newPassword = ''
  errors.confirmPassword = ''

  let isValid = true

  if (!form.newPassword) {
    errors.newPassword = 'Password is required'
    isValid = false
  } else if (form.newPassword.length < 6) {
    errors.newPassword = 'Password must be at least 6 characters'
    isValid = false
  }

  if (!form.confirmPassword) {
    errors.confirmPassword = 'Please confirm your password'
    isValid = false
  } else if (form.newPassword !== form.confirmPassword) {
    errors.confirmPassword = 'Passwords do not match'
    isValid = false
  }

  return isValid
}

const handleResetPassword = async () => {
  if (!validateForm()) {
    return
  }

  const token = route.query.token

  if (!token) {
    tokenError.value = 'Reset token is missing. Please request a new password reset link.'
    return
  }

  loading.value = true

  try {
    await http.post('/auth/reset-password', {
      token,
      newPassword: form.newPassword
    })

    resetComplete.value = true
    toast.success('Password reset successful!')
  } catch (err) {
    console.error('Reset password error:', err)

    if (err.code === 'INVALID_RESET_TOKEN') {
      tokenError.value = 'This reset link is invalid or has expired. Please request a new one.'
      toast.error(tokenError.value)
    } else if (err.code === 'WEAK_PASSWORD') {
      errors.newPassword = err.message
      toast.error(err.message)
    } else {
      // Don't disable the form for general errors
      toast.error(err.message || 'Failed to reset password. Please try again.')
    }
  } finally {
    loading.value = false
  }
}

// Lifecycle
onMounted(() => {
  const token = route.query.token
  if (!token) {
    tokenError.value = 'Reset token is missing. Please request a new password reset link.'
  }
})
</script>

